import React, { useId, useState } from 'react';
import { Lock, Send } from 'lucide-react';
import { AppButton, AppCard, Label, SegmentedControl, Textarea, cn } from '../ui';

const TEXT_MAX = 10000;
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

/**
 * TicketComposer — the reply box under a thread.
 *
 * Staff choose between a reply the requester reads and an internal note only
 * staff read; the box changes colour AND says which it is, because sending a
 * note to a student by mistake cannot be taken back. Ctrl/⌘ + Enter sends.
 *
 * Props:
 *   canWriteInternal — show the Reply / Internal note switch
 *   isDone           — the ticket is resolved or closed
 *   isRequester      — the viewer raised this ticket
 *   requesterName    — for the placeholder
 *   onSend           — ({ text, internal, resolve }) => Promise<boolean>;
 *                      resolves true when sent, and the box is cleared
 */
const TicketComposer = ({ canWriteInternal, isDone, isRequester, requesterName, onSend }) => {
  const [mode, setMode] = useState('reply');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const fieldId = useId();
  const hintId = useId();

  const internal = canWriteInternal && mode === 'note';
  const ready = text.trim().length > 0 && text.length <= TEXT_MAX && !sending;

  const send = async (resolve = false) => {
    if (!ready) return;
    setSending(true);
    const ok = await onSend({ text: text.trim(), internal, resolve });
    setSending(false);
    if (ok) setText('');
  };

  const reopens = isRequester && isDone && !internal;
  const primaryLabel = internal ? 'Add note' : reopens ? 'Send & reopen' : 'Send reply';
  const label = internal ? 'Internal note' : isRequester ? 'Your reply' : `Reply to ${requesterName || 'the requester'}`;

  return (
    <AppCard as="section" aria-label="Write a reply">
      {canWriteInternal && (
        <div
          className={cn(
            'tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-border-b tw-border-solid tw-px-4 tw-py-2',
            internal ? 'tw-border-warning/30 tw-bg-warning-soft' : 'tw-border-line-soft tw-bg-surface-alt',
          )}
        >
          <SegmentedControl
            label="Message type"
            size="xs"
            value={mode}
            onChange={setMode}
            options={[{ value: 'reply', label: 'Reply' }, { value: 'note', label: 'Internal note' }]}
          />
          {internal && (
            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-warning-fg">
              <Lock size={12} aria-hidden="true" /> Only staff can see this
            </span>
          )}
        </div>
      )}

      <form
        className="tw-flex tw-flex-col tw-gap-2 tw-p-4"
        onSubmit={(e) => { e.preventDefault(); send(false); }}
      >
        <Label htmlFor={fieldId}>{label}</Label>
        <Textarea
          id={fieldId}
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              send(false);
            }
          }}
          aria-describedby={hintId}
          invalid={text.length > TEXT_MAX}
          placeholder={internal
            ? 'Add context for your team — the requester never sees this.'
            : isRequester ? 'Add details, answer a question, or tell us it’s fixed.' : 'Write your reply…'}
        />
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <p id={hintId} className="tw-m-0 tw-text-xs tw-text-content-subtle">
            {text.length > TEXT_MAX - 500
              ? `${(TEXT_MAX - text.length).toLocaleString('en-IN')} characters left`
              : reopens
                ? 'Replying reopens this request.'
                : `${isMac ? '⌘' : 'Ctrl'} + Enter to send`}
          </p>
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {canWriteInternal && !internal && !isDone && (
              <AppButton type="button" size="sm" variant="outline" disabled={!ready} onClick={() => send(true)}>
                Send & resolve
              </AppButton>
            )}
            <AppButton type="submit" size="sm" disabled={!ready}>
              <Send size={16} aria-hidden="true" />
              {sending ? 'Sending…' : primaryLabel}
            </AppButton>
          </div>
        </div>
      </form>
    </AppCard>
  );
};

export default TicketComposer;
